import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { PublicPrerenderContext } from "./lib/publicPrerender";
import { useClerk, useUser } from "./lib/publicAuth";
import { afterPageIdle } from "./lib/idle";
import { afterFirstInteraction } from "./lib/interaction";
import { Switch, Route, useLocation, Router as WouterRouter, Redirect } from "wouter";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { getGetTrainingPreferencesQueryKey, getMyMinistry, useGetTrainingPreferences } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { LazyMotion, domAnimation } from "framer-motion";

const PastorSetup = lazy(() => import("./pages/PastorSetup"));
const PastorDashboard = lazy(() => import("./pages/PastorDashboard"));
const StudentSurvey = lazy(() => import("./pages/StudentSurvey"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminSetup = lazy(() => import("./pages/AdminSetup"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const ShopPage = lazy(() => import("./pages/ShopPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const SelfAssessmentPage = lazy(() => import("./pages/SelfAssessmentPage"));
const MinistryHealthPage = lazy(() => import("./pages/MinistryHealthPage"));
const CurriculumQuizPage = lazy(() => import("./pages/CurriculumQuizPage"));
const ExplorePage = lazy(() => import("./pages/ExplorePage"));
const ToolCenter = lazy(() => import("./pages/ToolCenter"));
const SuggestionsPage = lazy(() => import("./pages/SuggestionsPage"));
const DashboardPreview = lazy(() => import("./pages/DashboardPreview"));
const TrainingPage = lazy(() => import("./pages/TrainingPage"));
const TrainingCoursePage = lazy(() => import("./pages/TrainingCoursePage"));
const TrainingSetupPage = lazy(() => import("./pages/TrainingSetupPage"));
const MyTrainingPage = lazy(() => import("./pages/MyTrainingPage"));
const CurriculumDashboard = lazy(() => import("./pages/curriculum/CurriculumDashboard"));
const AIGenerator = lazy(() => import("./pages/curriculum/AIGenerator"));
const CurriculumLibrary = lazy(() => import("./pages/curriculum/CurriculumLibrary"));
const PackageDetail = lazy(() => import("./pages/curriculum/PackageDetail"));
const BuyTokens = lazy(() => import("./pages/curriculum/BuyTokens"));
const CurriculumSuggestions = lazy(() => import("./pages/curriculum/CurriculumSuggestions"));
const CurriculumReviews = lazy(() => import("./pages/curriculum/CurriculumReviews"));
// The homepage remains eagerly module-preloaded on its own document, rather
// than making every inner page download and evaluate its implementation.
import SiteHome from "./pages/SiteHome";
const AboutPage = lazy(() => import("./pages/AboutPage"));
const StatementOfFaithPage = lazy(() => import("./pages/StatementOfFaithPage"));
const WLDPage = lazy(() => import("./pages/WLDPage"));

// --- Clerk Setup ---
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
import { PublicRouteMetadata } from "./lib/seo";
import { SHOPIFY_PAGE_REDIRECTS, SHOPIFY_PRODUCT_REDIRECTS, SHOPIFY_PAGE_EXTERNAL } from "../server/routing.mjs";
import { trackShopifyPageView } from "./lib/shopifyAnalytics";
const AuthRuntime = lazy(() => import("./components/AuthRuntime"));
const ShopifyCookieRuntime = lazy(() => import("./components/ShopifyCookieRuntime"));
const SignIn = lazy(() => import("./components/AuthRuntime").then((m) => ({ default: m.SignIn })));
const SignUp = lazy(() => import("./components/AuthRuntime").then((m) => ({ default: m.SignUp })));

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");


const AssessmentTool = lazy(() => import("./pages/AssessmentTool"));

// --- Auth-aware Home Route ---

function HomeRoute() {
  return <SiteHome />;
}

// --- Protected Route Wrappers ---

function PastorRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useUser();
  if (!isLoaded) return null;
  if (!isSignedIn) return <Redirect to="/sign-in" />;
  return <>{children}</>;
}

function TrainingAccessRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [, setLocation] = useLocation();
  const [isRecoveringAuth, setIsRecoveringAuth] = useState(false);
  const preferences = useGetTrainingPreferences({
    query: { enabled: isSignedIn === true, retry: false, queryKey: getGetTrainingPreferencesQueryKey() },
  });

  const isUnauthorized =
    preferences.isError &&
    typeof preferences.error === "object" &&
    preferences.error !== null &&
    "status" in preferences.error &&
    preferences.error.status === 401;

  useEffect(() => {
    if (!isUnauthorized || isRecoveringAuth) return;

    const recoveryKey = "training-auth-recovery-attempted";
    if (sessionStorage.getItem(recoveryKey) === "true") return;

    sessionStorage.setItem(recoveryKey, "true");
    setIsRecoveringAuth(true);
    void signOut(() => {
      setLocation("/sign-in?redirect_url=%2Ftraining%2Fmy-courses", { replace: true });
    });
  }, [isUnauthorized, isRecoveringAuth, setLocation, signOut]);

  useEffect(() => {
    if (preferences.isSuccess) {
      sessionStorage.removeItem("training-auth-recovery-attempted");
    }
  }, [preferences.isSuccess]);

  const isWaitingForPreferences =
    isSignedIn && (preferences.isLoading || (preferences.isFetching && !preferences.data));

  if (!isLoaded || isWaitingForPreferences || isRecoveringAuth) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        {isRecoveringAuth && (
          <p className="text-sm font-semibold text-slate-600">Refreshing your sign-in…</p>
        )}
      </div>
    );
  }
  if (!isSignedIn) return <Redirect to="/sign-in?redirect_url=%2Ftraining%2Fmy-courses" />;
  if (preferences.isError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-2xl font-black text-slate-900">Let’s restart your sign-in.</h1>
          <p className="mt-3 text-slate-600">
            Your account was created, but the secure training session did not finish loading.
          </p>
          <Button
            className="mt-6 rounded-full px-7 font-bold"
            onClick={() => {
              sessionStorage.removeItem("training-auth-recovery-attempted");
              void signOut(() => {
                setLocation("/sign-in?redirect_url=%2Ftraining%2Fmy-courses", { replace: true });
              });
            }}
          >
            Restart Sign-In
          </Button>
        </div>
      </div>
    );
  }
  if (!preferences.data) return <Redirect to="/training/setup" />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useUser();
  const { data: ministry, isLoading } = useQuery({
    queryKey: ["ministry"],
    queryFn: () => getMyMinistry(),
    enabled: isSignedIn === true,
    retry: false,
  });
  if (!isLoaded || (isSignedIn && isLoading)) return null;
  if (!isSignedIn) return <Redirect to="/sign-in?redirect_url=%2Fadmin" />;
  if (!ministry) return <Redirect to="/admin-setup" />;
  if (ministry && !ministry.isAdmin) return <Redirect to="/dashboard" />;
  return <>{children}</>;
}

// --- Sign In / Sign Up Pages ---

function getSafeRedirectUrl(searchParams: URLSearchParams) {
  const url = searchParams.get("redirect_url");
  if (!url) return null;
  if (!url.startsWith("/") || url.startsWith("//") || url.startsWith("/\\")) {
    return null;
  }
  return url;
}

function SignInPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const redirectUrl = getSafeRedirectUrl(searchParams);
  const isAdminSignIn = redirectUrl === "/admin";
  const isTraining = redirectUrl?.startsWith("/training");
  
  const postSignInUrl = `${basePath}${redirectUrl || "/dashboard"}`;
  const signUpUrl = `${basePath}/sign-up${redirectUrl ? `?redirect_url=${encodeURIComponent(redirectUrl)}` : ""}`;

  return (
    <div className={`flex min-h-[100dvh] items-center justify-center px-4 ${isAdminSignIn ? "bg-slate-950" : "bg-slate-50"}`}>
      <div className="w-full max-w-md">
        <p className={`text-center text-sm mb-6 ${isAdminSignIn ? "text-slate-400" : "text-slate-500"}`}>
          {isAdminSignIn
            ? "Restricted to authorized administrators only."
            : isTraining
            ? "Sign in to access your training course."
            : "Sign in to access your youth leader dashboard."}
        </p>
        <SignIn
          routing="path"
          path={`${basePath}/sign-in`}
          signUpUrl={signUpUrl}
          fallbackRedirectUrl={postSignInUrl}
          {...(redirectUrl ? { forceRedirectUrl: postSignInUrl } : {})}
        />
      </div>
    </div>
  );
}

function SignUpPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const redirectUrl = getSafeRedirectUrl(searchParams);
  const isTraining = redirectUrl?.startsWith("/training");
  
  const postSignUpUrl = `${basePath}${redirectUrl || "/dashboard"}`;
  const signInUrl = `${basePath}/sign-in${redirectUrl ? `?redirect_url=${encodeURIComponent(redirectUrl)}` : ""}`;

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <p className="text-center text-sm text-slate-500 mb-6">
          {isTraining
            ? "Create a free account to access your training course."
            : "Create a free account to generate your student survey."}
        </p>
        <SignUp 
          routing="path" 
          path={`${basePath}/sign-up`} 
          signInUrl={signInUrl} 
          fallbackRedirectUrl={postSignUpUrl} 
          {...(redirectUrl ? { forceRedirectUrl: postSignUpUrl } : {})}
        />
      </div>
    </div>
  );
}

function AdminLoginPage() {
  const { isSignedIn, isLoaded } = useUser();

  if (!isLoaded) return null;
  return (
    <Redirect
      to={isSignedIn ? "/admin" : "/sign-in?redirect_url=%2Fadmin"}
      replace
    />
  );
}

// --- Cache Invalidator ---

// --- Main Router ---

function ScrollToTopOnNavigate() {
  const [location] = useLocation();
  const firstDocument = useRef(true);
  useEffect(() => {
    if (firstDocument.current) {
      firstDocument.current = false;
      // Fresh public documents already start at the top. A redundant scroll
      // during hydration can flush pending stylesheet/layout work. Retain the
      // existing behavior for reload/back, app shells and all client navigation.
      const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
      if (document.getElementById("root")?.dataset.publicPrerender === "true"
        && navigation?.type === "navigate") return;
    }
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}

function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function ShopifyPageRedirect({ handle }: { handle: string }) {
  const h = handle.split("/")[0];
  if (SHOPIFY_PAGE_EXTERNAL.has(h)) {
    return <ExternalRedirect to={`https://reframeyouth.myshopify.com/pages/${h}?_fd=0`} />;
  }
  const to = SHOPIFY_PAGE_REDIRECTS[h];
  if (to) return <RedirectWithQuery to={to} />;
  // Fall back to the same path (e.g. /pages/tools -> /tools), otherwise show 404.
  return <RedirectWithQuery to={`/${h}`} />;
}

/**
 * Shopify app-proxy paths (e.g. Filemonk download links: /apps/filemonk/...).
 * These must be served by Shopify, so forward the full path + query to the
 * myshopify domain, keeping every original query param (download tokens) and
 * adding _fd=0 so Shopify doesn't bounce back to the primary domain.
 */
function ShopifyAppProxyRedirect() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  params.set("_fd", "0");
  return (
    <ExternalRedirect
      to={`https://reframeyouth.myshopify.com${path}?${params.toString()}${window.location.hash}`}
    />
  );
}

function RedirectWithQuery({ to }: { to: string }) {
  const suffix = window.location.search + window.location.hash;
  return <Redirect to={`${to}${suffix}`} replace />;
}

function ShopifyAnalyticsTracker() {
  const [location] = useLocation();
  const [ready, setReady] = useState(false);
  useEffect(() => afterFirstInteraction(() => setReady(true)), []);
  useEffect(() => {
    // Product pages send their own product-typed page view with GIDs.
    if (location.startsWith("/products/") || location === "/wld") return;
    trackShopifyPageView();
  }, [location]);

  return ready ? <Suspense fallback={null}><ShopifyCookieRuntime /></Suspense> : null;
}

function GoogleAnalyticsPageViews() {
  const [location] = useLocation();

  useEffect(() => {
    const gtag = (window as typeof window & { gtag?: (...args: unknown[]) => void }).gtag;
    gtag?.("event", "page_view", {
      send_to: "G-9LV3BMNXWW",
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
    });
  }, [location]);

  return null;
}

function MetaPixelPageViews() {
  const [location] = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // index.html already fires the initial PageView; only track subsequent in-app navigations
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const fbq = (window as any).fbq;
    if (typeof fbq === "function") {
      fbq("track", "PageView");
    }
  }, [location]);

  return null;
}

function RouteAuth({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const required = /^\/(?:sign-in|sign-up|admin-login|setup|dashboard|admin(?:\/|$)|admin-setup|curriculum(?:\/|$)|training(?:\/|$)|suggestions(?:\/|$))/.test(location);
  const anonymousShare = /^\/(?:preview|survey)\//.test(location);
  const [observe, setObserve] = useState(false);
  useEffect(() => {
    // Anonymous marketing visits must not download Clerk at all. Existing
    // sessions resolve nav state at idle, without remounting the public page.
    if (required || anonymousShare) return;
    const check = () => {
      if (/(?:^|;\s*)__client_uat(?:_[a-zA-Z0-9]+)?=(?!0(?:;|$))[^;]+|(?:^|;\s*)__session(?:_[a-zA-Z0-9]+)?=[^;]+/.test(document.cookie)) setObserve(true);
    };
    const cancelIdle = afterPageIdle(check);
    // A visitor can sign in in another tab after this page became idle.
    const onFocus = () => { if (document.visibilityState === "visible") check(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelIdle();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [required, anonymousShare]);
  if (required) return <Suspense fallback={null}><AuthRuntime>{children}</AuthRuntime></Suspense>;
  return <>{children}{observe && !anonymousShare && <Suspense fallback={null}><AuthRuntime /></Suspense>}</>;
}

function AppRouter({ client = queryClient }: { client?: typeof queryClient }) {
  return (
      <QueryClientProvider client={client}>
       <RouteAuth>
        <ShopifyAnalyticsTracker />
        <GoogleAnalyticsPageViews />
        <MetaPixelPageViews />
        <ScrollToTopOnNavigate />
        <PublicRouteMetadata />
        <Suspense fallback={null}>
        <Switch>
          <Route path="/" component={HomeRoute} />
          <Route path="/pages/*">
            {(params) => <ShopifyPageRedirect handle={params["*"] ?? ""} />}
          </Route>
          <Route path="/collections/*">
            {(params) => {
              const h = (params["*"] ?? "").split("/")[0];
              return <RedirectWithQuery to={h === "wld" || h === "volume-2" ? "/wld" : "/shop"} />;
            }}
          </Route>
          <Route path="/blogs/:blog/:article">
            {(params) => <RedirectWithQuery to={`/blog/${params.article}`} />}
          </Route>
          <Route path="/blogs/*">{() => <RedirectWithQuery to="/blog" />}</Route>
          <Route path="/policies/:handle">
            {(params) => <ExternalRedirect to={`https://reframeyouth.myshopify.com/policies/${params.handle}?_fd=0`} />}
          </Route>
          <Route path="/apps/*?">{() => <ShopifyAppProxyRedirect />}</Route>
          <Route path="/a/*?">{() => <ShopifyAppProxyRedirect />}</Route>
          <Route path="/cart">{() => <RedirectWithQuery to="/shop" />}</Route>
          <Route path="/cart/*">{() => <RedirectWithQuery to="/shop" />}</Route>
          <Route path="/search">{() => <RedirectWithQuery to="/shop" />}</Route>
          <Route path="/account/*?">{() => <RedirectWithQuery to="/" />}</Route>
          <Route path="/about" component={AboutPage} />
          <Route path="/statement-of-faith" component={StatementOfFaithPage} />
          <Route path="/tools" component={ToolCenter} />
          <Route path="/training" component={TrainingPage} />
          <Route path="/training/setup">
            <PastorRoute><TrainingSetupPage /></PastorRoute>
          </Route>
          <Route path="/training/my-courses">
            <TrainingAccessRoute><MyTrainingPage /></TrainingAccessRoute>
          </Route>
          <Route path="/admin/course-preview/:courseSlug">
            {() => (
              <AdminRoute>
                <TrainingCoursePage adminPreview />
              </AdminRoute>
            )}
          </Route>
          <Route path="/training/:courseSlug">
            {() => <TrainingAccessRoute><TrainingCoursePage /></TrainingAccessRoute>}
          </Route>
          <Route path="/wld" component={WLDPage} />
          <Route path="/shop" component={ShopPage} />
          <Route path="/products/chosen-finding-your-path-in-faith">
            {() => <RedirectWithQuery to={SHOPIFY_PRODUCT_REDIRECTS["chosen-finding-your-path-in-faith"]} />}
          </Route>
          <Route path="/products/love-logics">
            {() => <RedirectWithQuery to={SHOPIFY_PRODUCT_REDIRECTS["love-logics"]} />}
          </Route>
          <Route path="/products/whole-life-discipleship-curriculum-volume-3">
            {() => <RedirectWithQuery to={SHOPIFY_PRODUCT_REDIRECTS["whole-life-discipleship-curriculum-volume-3"]} />}
          </Route>
          <Route path="/products/:handle" component={ProductPage} />
          <Route path="/blog" component={BlogPage} />
          <Route path="/blog/:handle" component={BlogPostPage} />
          <Route path="/first-aid-kit" component={LandingPage} />
          <Route path="/self-assessment" component={SelfAssessmentPage} />
          <Route path="/ministry-survey" component={MinistryHealthPage} />
          <Route path="/curriculum-quiz" component={CurriculumQuizPage} />
          <Route path="/build-bundle" component={ExplorePage} />
          <Route path="/series/*">{() => <RedirectWithQuery to="/shop" />}</Route>
          <Route path="/series">{() => <RedirectWithQuery to="/shop" />}</Route>
          <Route path="/assessment" component={AssessmentTool} />
          <Route path="/suggestions" component={SuggestionsPage} />
          <Route path="/sign-in/*?" component={SignInPage} />
          <Route path="/sign-up/*?" component={SignUpPage} />
          <Route path="/admin-login/*?" component={AdminLoginPage} />
          <Route path="/setup">
            <PastorRoute><PastorSetup /></PastorRoute>
          </Route>
          <Route path="/dashboard">
            <PastorRoute><PastorDashboard /></PastorRoute>
          </Route>
          <Route path="/admin">
            <AdminRoute><AdminDashboard /></AdminRoute>
          </Route>
          <Route path="/admin-setup">
            <PastorRoute><AdminSetup /></PastorRoute>
          </Route>
          <Route path="/preview/:code" component={DashboardPreview} />
          <Route path="/survey/:code" component={StudentSurvey} />
          <Route path="/curriculum">
            <PastorRoute><CurriculumDashboard /></PastorRoute>
          </Route>
          <Route path="/curriculum/generate">
            <PastorRoute><AIGenerator /></PastorRoute>
          </Route>
          <Route path="/curriculum/library">
            <PastorRoute><CurriculumLibrary /></PastorRoute>
          </Route>
          <Route path="/curriculum/package/:id">
            <PastorRoute><PackageDetail /></PastorRoute>
          </Route>
          <Route path="/curriculum/buy-tokens">
            <PastorRoute><BuyTokens /></PastorRoute>
          </Route>
          <Route path="/curriculum/suggestions">
            <PastorRoute><CurriculumSuggestions /></PastorRoute>
          </Route>
          <Route path="/curriculum/reviews">
            <PastorRoute><CurriculumReviews /></PastorRoute>
          </Route>
          <Route>
            <NotFoundPage />
          </Route>
        </Switch>
        </Suspense>
       </RouteAuth>
      </QueryClientProvider>
  );
}


export default function App({ ssrPath, client, prerender = false }: { ssrPath?: string; client?: typeof queryClient; prerender?: boolean } = {}) {
  return (
    <PublicPrerenderContext.Provider value={prerender}>
    <LazyMotion features={domAnimation}>
    <WouterRouter base={basePath} ssrPath={ssrPath}>
      <AppRouter client={client} />
    </WouterRouter>
    </LazyMotion>
    </PublicPrerenderContext.Provider>
  );
}
